/**
 * Google Translate and some extensions replace text nodes while React is committing.
 * React then calls insertBefore/removeChild on a node that is no longer a child and
 * the page crashes (NotFoundError). Reattach by appending, or no-op a stale remove.
 *
 * @see ST-2026-226
 */
export function patchReactDomMutations(): void {
  if (typeof Node === 'undefined' || typeof Node.prototype.insertBefore !== 'function') {
    return;
  }
  const proto = Node.prototype as Node & { __aptDomMutationPatched?: boolean };
  if (proto.__aptDomMutationPatched) {
    return;
  }
  proto.__aptDomMutationPatched = true;

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };
}
